import React, { useState, useRef, useEffect } from 'react';
import { Search, User, Calendar, X, ChevronDown } from 'lucide-react';

/**
 * Parse date from DD/MM/YYYY or ISO format
 */
function parseDate(dateString) {
  if (!dateString) return null;
  if (dateString instanceof Date) return dateString;

  const str = String(dateString).trim();

  if (/^\d{2}\/\d{2}\/\d{4}$/.test(str)) {
    const [day, month, year] = str.split('/');
    return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
  }

  if (/^\d{2}-\d{2}-\d{4}$/.test(str)) {
    const [day, month, year] = str.split('-');
    return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
  }

  if (/^\d{4}-\d{2}-\d{2}/.test(str)) {
    const [year, month, day] = str.substring(0, 10).split('-');
    return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
  }

  return new Date(str);
}

export default function EmployeeSelector({
  employees,
  selectedEmployee,
  onEmployeeChange,
  empleadoTomoVacaciones,
  onTomoVacacionesChange,
  usarFechasPersonalizadas,
  onUsarFechasPersonalizadasChange,
  fechaIngresoPersonalizada,
  onFechaIngresoChange,
  fechaSalidaPersonalizada,
  onFechaSalidaChange,
  incluirPreaviso,
  onIncluirPreavisoChange,
  incluirCesantia,
  onIncluirCesantiaChange
}) {
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  const selectedEmp = employees.find(e => e.id === selectedEmployee);

  // Calcular años, meses y días de servicio
  let anosEmp = 0, mesesEmp = 0, diasEmp = 0;
  if (selectedEmp) {
    const fechaIngresoField = selectedEmp.fechaIngreso || selectedEmp.fecha_ingreso;
    if (fechaIngresoField) {
      const fechaIngreso = parseDate(fechaIngresoField);
      const hoy = new Date();
      if (fechaIngreso && !isNaN(fechaIngreso.getTime())) {
        anosEmp = hoy.getFullYear() - fechaIngreso.getFullYear();
        mesesEmp = hoy.getMonth() - fechaIngreso.getMonth();
        if (mesesEmp < 0) { anosEmp--; mesesEmp += 12; }
        if (hoy.getDate() < fechaIngreso.getDate()) {
          mesesEmp--;
          if (mesesEmp < 0) { anosEmp--; mesesEmp += 12; }
        }
        anosEmp = Math.max(0, anosEmp);
        mesesEmp = Math.max(0, mesesEmp);
        const fechaBase = new Date(fechaIngreso);
        fechaBase.setFullYear(fechaBase.getFullYear() + anosEmp);
        fechaBase.setMonth(fechaBase.getMonth() + mesesEmp);
        diasEmp = Math.max(0, Math.floor((hoy - fechaBase) / (1000 * 60 * 60 * 24)));
      }
    }
  }

  // Formatear fecha sin conversión UTC (evita el día de diferencia por timezone)
  const formatFechaIngreso = (dateStr) => {
    if (!dateStr) return 'N/A';
    const parsed = parseDate(String(dateStr));
    if (!parsed || isNaN(parsed.getTime())) return String(dateStr);
    const d = String(parsed.getDate()).padStart(2, '0');
    const m = String(parsed.getMonth() + 1).padStart(2, '0');
    return `${d}/${m}/${parsed.getFullYear()}`;
  };

  // Cerrar al hacer click fuera
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredEmployees = employees
    .filter(e => e.estado === 'activo')
    .filter(e => {
      if (!searchTerm) return true;
      const name = `${e.nombre} ${e.apellido}`.toLowerCase();
      return name.includes(searchTerm.toLowerCase()) ||
             (e.employee_id || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
             (e.cedula || '').includes(searchTerm);
    })
    .sort((a, b) => `${a.nombre} ${a.apellido}`.localeCompare(`${b.nombre} ${b.apellido}`));

  const handleSelect = (empId) => {
    onEmployeeChange(empId);
    setSearchTerm('');
    setIsOpen(false);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onEmployeeChange('');
    setSearchTerm('');
    setIsOpen(false);
  };

  const openSearch = () => {
    setIsOpen(true);
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
      {/* Searchable employee selector */}
      <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Empleado</label>

      <div className="relative" ref={containerRef}>
        {/* Trigger: muestra empleado seleccionado o campo de búsqueda */}
        {selectedEmp && !isOpen ? (
          <button
            type="button"
            onClick={openSearch}
            className="w-full flex items-center gap-3 px-3 py-2.5 border border-gray-200 rounded-lg hover:border-gray-300 transition-colors text-left"
          >
            <div className="bg-blue-50 rounded-full p-1.5 flex-shrink-0">
              <User className="w-3.5 h-3.5 text-blue-500" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-900 truncate">
                {selectedEmp.nombre} {selectedEmp.apellido}
              </p>
              <p className="text-xs text-gray-400 truncate">
                {selectedEmp.departamento} · {selectedEmp.cargo}
              </p>
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              <button
                onClick={handleClear}
                className="p-1 hover:bg-gray-100 rounded transition-colors"
                title="Cambiar empleado"
              >
                <X className="w-3.5 h-3.5 text-gray-400" />
              </button>
              <ChevronDown className="w-3.5 h-3.5 text-gray-300" />
            </div>
          </button>
        ) : (
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            <input
              ref={inputRef}
              type="text"
              placeholder="Buscar empleado por nombre..."
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setIsOpen(true); }}
              onFocus={() => setIsOpen(true)}
              className="w-full pl-9 pr-4 py-2.5 border border-blue-400 rounded-lg text-sm outline-none"
            />
          </div>
        )}

        {/* Dropdown */}
        {isOpen && (
          <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden">
            <div className="max-h-60 overflow-y-auto">
              {filteredEmployees.length === 0 ? (
                <div className="px-4 py-8 text-center text-sm text-gray-400">
                  Sin resultados para "{searchTerm}"
                </div>
              ) : (
                filteredEmployees.map(emp => (
                  <button
                    key={emp.id}
                    type="button"
                    onClick={() => handleSelect(emp.id)}
                    className={`w-full text-left px-4 py-2.5 hover:bg-gray-50 transition-colors border-b border-gray-50 last:border-0 ${
                      emp.id === selectedEmployee ? 'bg-blue-50' : ''
                    }`}
                  >
                    <p className="text-sm font-medium text-gray-900">{emp.nombre} {emp.apellido}</p>
                    <p className="text-xs text-gray-400">{emp.departamento} · {emp.cargo}</p>
                  </button>
                ))
              )}
            </div>
            <div className="px-4 py-2 border-t border-gray-100 bg-gray-50">
              <p className="text-xs text-gray-400">{filteredEmployees.length} empleado{filteredEmployees.length !== 1 ? 's' : ''}</p>
            </div>
          </div>
        )}
      </div>

      {/* Info del empleado seleccionado */}
      {selectedEmp && !isOpen && (
        <div className="mt-3 grid grid-cols-2 gap-2">
          <div className="bg-gray-50 rounded-lg p-2.5">
            <p className="text-[10px] text-gray-400 uppercase tracking-wider">Antigüedad</p>
            <p className="text-xs font-semibold text-gray-700 mt-0.5">
              {anosEmp} año{anosEmp !== 1 ? 's' : ''}, {mesesEmp} mes{mesesEmp !== 1 ? 'es' : ''}, {diasEmp} día{diasEmp !== 1 ? 's' : ''}
            </p>
          </div>
          <div className="bg-gray-50 rounded-lg p-2.5">
            <p className="text-[10px] text-gray-400 uppercase tracking-wider">Ingreso</p>
            <p className="text-xs font-semibold text-gray-700 mt-0.5">
              {formatFechaIngreso(selectedEmp.fechaIngreso || selectedEmp.fecha_ingreso)}
            </p>
          </div>
        </div>
      )}

      {/* Opciones de cálculo — visible solo con empleado seleccionado */}
      {selectedEmp && !isOpen && (
        <div className="mt-4 space-y-3 pt-3 border-t border-gray-100">
          <h4 className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Opciones de Cálculo</h4>

          {/* Vacaciones */}
          {anosEmp >= 1 ? (
            <label className="flex items-start gap-2.5 p-2.5 bg-gray-50 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors">
              <input
                type="checkbox"
                checked={empleadoTomoVacaciones}
                onChange={(e) => onTomoVacacionesChange(e.target.checked)}
                className="mt-0.5 w-4 h-4 text-blue-600 rounded focus:ring-blue-500 flex-shrink-0"
              />
              <div>
                <span className="text-xs font-medium text-gray-700 block">Ya tomó vacaciones</span>
                <span className="text-xs text-gray-400">Pago proporcional desde último aniversario</span>
              </div>
            </label>
          ) : (
            <div className="p-2.5 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-xs text-amber-700">Menos de 1 año — no aplica vacaciones (Art. 178)</p>
            </div>
          )}

          {/* Preaviso y Cesantía */}
          <div className="grid grid-cols-2 gap-2">
            <label className="flex items-center gap-2 p-2 bg-gray-50 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors">
              <input
                type="checkbox"
                checked={incluirPreaviso}
                onChange={(e) => onIncluirPreavisoChange(e.target.checked)}
                className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500 flex-shrink-0"
              />
              <span className="text-xs font-medium text-gray-700">Preaviso</span>
            </label>
            <label className="flex items-center gap-2 p-2 bg-gray-50 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors">
              <input
                type="checkbox"
                checked={incluirCesantia}
                onChange={(e) => onIncluirCesantiaChange(e.target.checked)}
                className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500 flex-shrink-0"
              />
              <span className="text-xs font-medium text-gray-700">Cesantía</span>
            </label>
          </div>

          {/* Fechas personalizadas */}
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <label className="flex items-center gap-2 px-3 py-2.5 cursor-pointer hover:bg-gray-50 transition-colors">
              <input
                type="checkbox"
                checked={usarFechasPersonalizadas}
                onChange={(e) => onUsarFechasPersonalizadasChange(e.target.checked)}
                className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
              />
              <Calendar className="w-3.5 h-3.5 text-gray-400" />
              <span className="text-xs font-medium text-gray-700">Fechas personalizadas</span>
            </label>

            {usarFechasPersonalizadas && (
              <div className="px-3 pb-3 pt-1 border-t border-gray-100 grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Ingreso</label>
                  <input
                    type="date"
                    value={fechaIngresoPersonalizada}
                    onChange={(e) => onFechaIngresoChange(e.target.value)}
                    className="w-full px-2 py-1.5 border border-gray-200 rounded text-xs focus:border-blue-400 outline-none"
                  />
                  <p className="text-[10px] text-gray-400 mt-0.5">Vacío = del empleado</p>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Salida</label>
                  <input
                    type="date"
                    value={fechaSalidaPersonalizada}
                    onChange={(e) => onFechaSalidaChange(e.target.value)}
                    className="w-full px-2 py-1.5 border border-gray-200 rounded text-xs focus:border-blue-400 outline-none"
                    max={new Date().toISOString().split('T')[0]}
                  />
                  <p className="text-[10px] text-gray-400 mt-0.5">Por defecto = hoy</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
